import type { ComponentProps, SyntheticEvent } from 'react';
import React, { useState } from 'react';
import { AppBar, Tab, Tabs } from '@mui/material';

import styles from './FeatureManager.module.css';
import { FeatureList } from './FeatureList';
import { ToggleFeature } from './ToggleFeature';

type TabValues = 'feature_list' | 'enable_feature' | 'disable_feature';

type TabProps = {
  label: string;
  value: TabValues;
  activeTab: TabValues;
};

const tabProps = ({
  label,
  value,
  activeTab,
}: TabProps): ComponentProps<typeof Tab> => ({
  id: `tab-${value}`,
  'aria-controls': `tabpanel-${value}`,
  className: activeTab === value ? styles.activeTab : '',
  label,
  value,
});

export const FeatureManager = (): JSX.Element => {
  const [activeTab, setActiveTab] = useState<TabValues>('feature_list');

  const handleTabChange = (_e: SyntheticEvent, value: TabValues) => {
    setActiveTab(value);
  };

  return (
    <div className={styles.container}>
      <h2>Feature Manager</h2>
      <p>To enable/disable a feature for associated accounts:</p>
      <ul>
        <li>Select ENABLE FEATURE or DISABLE FEATURE.</li>
        <li>Select a feature.</li>
        <li>
          Enter a list of Account Owner emails or Account IDs separated by
          either a new line or comma.
        </li>
      </ul>

      <div>
        <AppBar position="relative">
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="secondary"
            textColor="inherit"
          >
            <Tab
              {...tabProps({
                label: 'Feature List',
                value: 'feature_list',
                activeTab,
              })}
            />
            <Tab
              {...tabProps({
                label: 'Enable Feature',
                value: 'enable_feature',
                activeTab,
              })}
            />
            <Tab
              {...tabProps({
                label: 'Disable Feature',
                value: 'disable_feature',
                activeTab,
              })}
            />
          </Tabs>
        </AppBar>
        {activeTab === 'feature_list' && <FeatureList />}
        {activeTab === 'enable_feature' && (
          <ToggleFeature action="enable_feature" />
        )}
        {activeTab === 'disable_feature' && (
          <ToggleFeature action="disable_feature" />
        )}
      </div>
    </div>
  );
};
